import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  Divider,
  Paper,
} from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import TermsViewerModal from './TermsViewerModal';
import type { TermsType, TermsAgreementState } from '../../types/terms';

interface Props {
  value: TermsAgreementState;
  onChange: (state: TermsAgreementState) => void;
}

const CHECKBOX_SX = {
  // 시니어 UX: 터치 영역 48px 이상
  '& .MuiSvgIcon-root': { fontSize: 28 },
  padding: '10px',
};

const LABEL_SX = {
  fontSize: '1rem', // 16px 이상
  fontWeight: 500,
};

const TermsAgreementSection: React.FC<Props> = ({ value, onChange }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTermsType, setActiveTermsType] = useState<TermsType | null>(null);

  const allChecked = value.serviceTerms && value.privacyConsent && value.marketingConsent;
  const allRequiredChecked = value.serviceTerms && value.privacyConsent;

  const handleAllChange = (checked: boolean) => {
    onChange({ serviceTerms: checked, privacyConsent: checked, marketingConsent: checked });
  };

  const handleIndividualChange = (key: keyof TermsAgreementState, checked: boolean) => {
    onChange({ ...value, [key]: checked });
  };

  const openModal = (type: TermsType) => {
    setActiveTermsType(type);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setActiveTermsType(null);
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ borderRadius: 2, overflow: 'hidden', mt: 3 }}
      >
        {/* 전체 동의 */}
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'grey.50',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={allChecked}
                indeterminate={!allChecked && (value.serviceTerms || value.privacyConsent || value.marketingConsent)}
                onChange={(e) => handleAllChange(e.target.checked)}
                sx={CHECKBOX_SX}
                inputProps={{ 'aria-label': '전체 동의' }}
              />
            }
            label={
              <Typography sx={{ ...LABEL_SX, fontWeight: 700, fontSize: '1.05rem' }}>
                전체 동의
              </Typography>
            }
            sx={{ m: 0, flex: 1 }}
          />
        </Box>

        <Divider />

        {/* 이용약관 (필수) */}
        <TermsRow
          checked={value.serviceTerms}
          onChange={(c) => handleIndividualChange('serviceTerms', c)}
          label="서비스 이용약관 동의"
          required
          onView={() => openModal('SERVICE_TERMS')}
          checkboxSx={CHECKBOX_SX}
          labelSx={LABEL_SX}
        />

        <Divider />

        {/* 개인정보 (필수) */}
        <TermsRow
          checked={value.privacyConsent}
          onChange={(c) => handleIndividualChange('privacyConsent', c)}
          label="개인정보 수집·이용 동의"
          required
          onView={() => openModal('PRIVACY_CONSENT')}
          checkboxSx={CHECKBOX_SX}
          labelSx={LABEL_SX}
        />

        <Divider />

        {/* 마케팅 (선택) */}
        <TermsRow
          checked={value.marketingConsent}
          onChange={(c) => handleIndividualChange('marketingConsent', c)}
          label="마케팅 정보 수신 동의"
          required={false}
          onView={() => openModal('MARKETING_CONSENT')}
          checkboxSx={CHECKBOX_SX}
          labelSx={LABEL_SX}
        />
      </Paper>

      {/* 필수 미동의 안내 */}
      {!allRequiredChecked && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, ml: 1, fontSize: '0.875rem' }}
        >
          * 필수 항목에 동의하셔야 가입하기 버튼이 활성화됩니다.
        </Typography>
      )}

      <TermsViewerModal
        open={modalOpen}
        termsType={activeTermsType}
        onClose={closeModal}
      />
    </>
  );
};

// ─── 개별 약관 행 ────────────────────────────────────────────────────────────

interface TermsRowProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  required: boolean;
  onView: () => void;
  checkboxSx: object;
  labelSx: object;
}

const TermsRow: React.FC<TermsRowProps> = ({
  checked,
  onChange,
  label,
  required,
  onView,
  checkboxSx,
  labelSx,
}) => (
  <Box sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center' }}>
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          sx={checkboxSx}
          inputProps={{ 'aria-label': label }}
        />
      }
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={labelSx}>{label}</Typography>
          <Typography
            component="span"
            sx={{
              fontSize: '0.85rem',
              color: required ? 'error.main' : 'text.secondary',
              fontWeight: 500,
            }}
          >
            ({required ? '필수' : '선택'})
          </Typography>
        </Box>
      }
      sx={{ m: 0, flex: 1 }}
    />
    <Button
      size="small"
      endIcon={<ChevronRight />}
      onClick={onView}
      sx={{
        minWidth: 52,
        fontSize: '0.875rem',
        color: 'text.secondary',
        flexShrink: 0,
        '&:hover': { color: 'primary.main' },
      }}
    >
      보기
    </Button>
  </Box>
);

export default TermsAgreementSection;
